import {
  Box,
  filledInputClasses,
  Grid,
  inputLabelClasses,
  styled,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Control, Controller, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IconQuestion } from 'assets/img';
import { StyledErrorText, StyledHelperTextWrapper } from 'redesign';
import { CompanySSOSettingsForm } from './edit-company-sso.types';

const USER_POOL_EMAIL_ATTRIBUTE = 'email';

type SsoAttributeMappingProps = {
  control: Control<CompanySSOSettingsForm>;
  samlAttributeName: Path<CompanySSOSettingsForm>;
  required: boolean;
  'data-testid'?: string;
};

export const SsoAttributeMapping = ({
  control,
  samlAttributeName,
  required,
  'data-testid': dataTestId,
}: SsoAttributeMappingProps) => {
  const { t } = useTranslation('ogp-screen-specific');

  return (
    <Box data-testid={dataTestId}>
      <TitleRow>
        <Typography variant="h3">{t('edit-sso-settings.mapping.title')}</Typography>
      </TitleRow>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <StyledTextField
            label={t('edit-sso-settings.user-pool-attribute.label')}
            value={USER_POOL_EMAIL_ATTRIBUTE}
            variant="filled"
            disabled
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name={samlAttributeName}
            shouldUnregister
            control={control}
            rules={
              required ? { required: t('edit-sso-settings.saml-attribute.required') } : undefined
            }
            render={({ field, fieldState: { error } }) => (
              <Box>
                <StyledTextField
                  {...field}
                  label={
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1.5,
                      }}
                    >
                      {t('edit-sso-settings.saml-attribute.label')}

                      <Tooltip
                        describeChild
                        title={t('edit-sso-settings.mapping.info.tooltip')}
                        placement="top"
                      >
                        <Box
                          component="span"
                          tabIndex={0}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            lineHeight: 0,
                            cursor: 'help',
                          }}
                        >
                          <IconQuestion width={16} height={16} />
                        </Box>
                      </Tooltip>
                    </Box>
                  }
                  placeholder={t('edit-sso-settings.saml-attribute.placeholder')}
                  variant="filled"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  required={required}
                  error={!!error}
                />
                <StyledHelperTextWrapper className="error-text-wrapper">
                  {error && (
                    <StyledErrorText className={error ? 'error-state' : ''}>
                      {error.message}
                    </StyledErrorText>
                  )}
                </StyledHelperTextWrapper>
              </Box>
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

const TitleRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  [`& .${filledInputClasses.root}`]: {
    border: `2px solid ${theme.palette.lwPrimaryAccentDark[40]}`,
    borderRadius: theme.spacing(3),
    '&:hover, &.Mui-focused': {
      borderColor: theme.palette.lwPrimaryAccentLight[100],
      boxShadow: `0 0 0 4px ${theme.palette.lwPrimaryAccentLight[10]}`,
    },
    '&.Mui-error': {
      borderColor: theme.palette.lwDanger[100],
      boxShadow: `0 0 0 4px ${theme.palette.lwDanger[20]}`,
    },
  },
  [`& .${inputLabelClasses.shrink}`]: {
    top: theme.spacing(-0.375),
  },
}));
